/**
 * @description Numeric keypad component for PIN entry
 *
 * Key features:
 * - Large touch targets (min 48px, optimized for tablet kiosk)
 * - 0-9 digits + backspace + enter
 * - Visual feedback on press
 * - Accessible button labels
 */

import React from 'react';
import { Delete, ArrowRight } from 'lucide-react';

const NumericKeypad = ({ onDigit, onBackspace, onSubmit, disabled }) => {
  const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['backspace', '0', 'enter'],
  ];

  const handleKey = (key) => {
    if (disabled) return;
    if (key === 'backspace') onBackspace();
    else if (key === 'enter') onSubmit();
    else onDigit(key);
  };

  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full max-w-[320px] sm:max-w-[360px] mx-auto">
      {keys.flat().map((key) => {
        if (key === 'backspace') {
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleKey(key)}
              disabled={disabled}
              className="group flex items-center justify-center h-16 sm:h-20 rounded-2xl bg-slate-800/50 hover:bg-slate-700/50 active:scale-95 border border-slate-700/50 transition-all duration-200 disabled:opacity-50 disabled:active:scale-100"
              aria-label="Backspace"
            >
              <Delete className="w-6 h-6 text-slate-400 group-hover:text-slate-200 transition-colors" />
            </button>
          );
        }
        if (key === 'enter') {
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleKey(key)}
              disabled={disabled}
              className="group flex items-center justify-center h-16 sm:h-20 rounded-2xl bg-primary-600 hover:bg-primary-500 active:scale-95 border border-primary-500/50 shadow-lg shadow-primary-900/20 transition-all duration-200 disabled:opacity-50 disabled:active:scale-100"
              aria-label="Submit"
            >
              <ArrowRight className="w-6 h-6 text-white group-hover:translate-x-0.5 transition-transform" />
            </button>
          );
        }
        return (
          <button
            key={key}
            type="button"
            onClick={() => handleKey(key)}
            disabled={disabled}
            className="group flex items-center justify-center h-16 sm:h-20 rounded-2xl bg-slate-800/50 hover:bg-slate-700/50 active:scale-95 border border-slate-700/50 transition-all duration-200 text-2xl sm:text-3xl font-semibold text-white disabled:opacity-50 disabled:active:scale-100"
          >
            <span className="group-hover:scale-110 transition-transform">{key}</span>
          </button>
        );
      })}
    </div>
  );
};

export default NumericKeypad;
